import { Raycaster, Object3D, Scene } from "three";
import { MouseClickType, ClickType } from "./ObjectEvents.js";
import { DragUtility } from "./DragUtility.js";
import { EventsDispatcher } from "./EventsDispatcher.js";
import { CursorHandler } from "./CursorHandler.js";
import { Globals } from "../Utils/Globals.js";
class RaycasterHandlerInitializer {
    static _init = false;
    static bindVectorComponentChange(vec, eventsDispatcher, name) {
        vec._x = vec.x;
        vec._y = vec.y;
        vec._z = vec.z;
        vec.parentEventHandler = eventsDispatcher;
        const eventName = name === "position" ? "onPositionComponentChange" : "onScaleComponentChange";
        Object.defineProperties(vec, {
            x: {
                get: function () { return this._x; },
                set: function (value) {
                    if (this._x !== value) {
                        this._x = value;
                        this.parentEventHandler.trigger(eventName, { component: "x", mesh: this.parentEventHandler._object3D });
                    }
                }
            },
            y: {
                get: function () { return this._y; },
                set: function (value) {
                    if (this._y !== value) {
                        this._y = value;
                        this.parentEventHandler.trigger(eventName, { component: "y", mesh: this.parentEventHandler._object3D });
                    }
                }
            },
            z: {
                get: function () { return this._z; },
                set: function (value) {
                    if (this._z !== value) {
                        this._z = value;
                        this.parentEventHandler.trigger(eventName, { component: "z", mesh: this.parentEventHandler._object3D });
                    }
                }
            }
        });
    }
    static bindEulerComponentChange(euler, eventsDispatcher) {
        euler.__x = 0;
        euler.__y = 0;
        euler.__z = 0;
        euler.parentEventHandler = eventsDispatcher;
        Object.defineProperties(euler, {
            _x: {
                get: function () { return this.__x; },
                set: function (value) {
                    if (this.__x !== value) {
                        this.__x = value;
                        this.parentEventHandler.trigger("onRotationComponentChange", { component: "x", mesh: this.parentEventHandler._object3D });
                    }
                }
            },
            _y: {
                get: function () { return this.__y; },
                set: function (value) {
                    if (this.__y !== value) {
                        this.__y = value;
                        this.parentEventHandler.trigger("onRotationComponentChange", { component: "y", mesh: this.parentEventHandler._object3D });
                    }
                }
            },
            _z: {
                get: function () { return this.__z; },
                set: function (value) {
                    if (this.__z !== value) {
                        this.__z = value;
                        this.parentEventHandler.trigger("onRotationComponentChange", { component: "z", mesh: this.parentEventHandler._object3D });
                    }
                }
            }
        });
    }
    static edit3DObjectPrototype() {
        if (!this._init) {
            this._init = true;
            Object.defineProperties(Object3D.prototype, {
                areAllParentsEnabled: {
                    get: function () {
                        let objToCheck = this.parent;
                        while (objToCheck) {
                            if (!objToCheck._isEnabled) {
                                return false;
                            }
                            objToCheck = objToCheck.parent;
                        }
                        return true;
                    }
                },
                areAllEnabled: {
                    get: function () {
                        return this._isEnabled && this.areAllParentsEnabled;
                    }
                },
                areAllParentsVisible: {
                    get: function () {
                        let objToCheck = this.parent;
                        while (objToCheck) {
                            if (!objToCheck._visible) {
                                return false;
                            }
                            objToCheck = objToCheck.parent;
                        }
                        return true;
                    }
                },
                areAllVisible: {
                    get: function () {
                        return this._visible && this.areAllParentsVisible;
                    }
                },
                //hack to understand when obj is initialized because in constructor this will be init for last. Look out when update three.js
                userData: {
                    get: function () { return this._userData; },
                    set: function (value) {
                        if (!this._eventHandler) {
                            this._eventHandler = new EventsDispatcher(this);
                            RaycasterHandlerInitializer.bindVectorComponentChange(this.position, this._eventHandler, "position");
                            RaycasterHandlerInitializer.bindVectorComponentChange(this.scale, this._eventHandler, "scale");
                            RaycasterHandlerInitializer.bindEulerComponentChange(this.rotation, this._eventHandler);
                        }
                        this._userData = value;
                    }
                },
                isEnabled: {
                    get: function () { return this._isEnabled; },
                    set: function (value) {
                        if (this._isEnabled !== value) {
                            this.triggerEnabledEventChild(this, value);
                            this._isEnabled = value;
                        }
                    }
                },
                visible: {
                    get: function () { return this._visible; },
                    set: function (value) {
                        if (this._visible !== value) {
                            this.triggerVisibleEventChild(this, value);
                            this._visible = value;
                            this.needsRender = true;
                        }
                    }
                },
                needsRender: {
                    get: function () { return this._needsRender; },
                    set: function (value) {
                        if (value || this instanceof Scene) {
                            let objToCheck = this;
                            while (objToCheck.parent) {
                                objToCheck = objToCheck.parent;
                            }
                            objToCheck._needsRender = value;
                        }
                    }
                }
            });
            Object3D.prototype.interceptedByRaycaster = true;
            Object3D.prototype.isDraggable = false;
            Object3D.prototype._isEnabled = true;
            Object3D.prototype._visible = true;
            Object3D.prototype.isDragging = false;
            Object3D.prototype.isHovered = false;
            Object3D.prototype.isPressed = [];
            Object3D.prototype.baseAdd = Object3D.prototype.add;
            Object3D.prototype.add = function (...objects) {
                const boundBaseAdd = Object3D.prototype.baseAdd.bind(this);
                boundBaseAdd(...objects);
                this._eventHandler.trigger("onChildAdded", objects);
                return this;
            };
            Object3D.prototype.triggerEnabledEventChild = function (obj, value) {
                obj._eventHandler.triggerWithoutPropagation("onEnabledChange", value);
                for (const child of obj.children) {
                    child._isEnabled && this.triggerEnabledEventChild(child, value);
                }
            };
            Object3D.prototype.triggerVisibleEventChild = function (obj, value) {
                obj._eventHandler.triggerWithoutPropagation("onVisibleChange", value);
                for (const child of obj.children) {
                    child._visible && this.triggerVisibleEventChild(child, value);
                }
            };
            Object3D.prototype.bindEvent = function (eventName, eventCallback) {
                this._eventHandler.bind(eventName, eventCallback);
                return eventCallback;
            };
            Object3D.prototype.unbindEvent = function (eventName, eventCallback) {
                this._eventHandler.unbind(eventName, eventCallback);
                return this;
            };
            Object3D.prototype.triggerEvent = function (eventName, args) {
                this._eventHandler.trigger(eventName, args);
            };
            Object3D.prototype.triggerWithoutPropagation = function (eventName, args) {
                this._eventHandler.triggerWithoutPropagation(eventName, args);
            };
            Object3D.prototype.registerHitbox = function (hitbox, position) {
                if (this.objectsToRaycast === undefined) {
                    this.objectsToRaycast = [hitbox];
                }
                else {
                    this.objectsToRaycast.push(hitbox);
                }
                hitbox.attachTo(this, position);
                return this;
            };
            Object3D.prototype.registerHitboxTouch = function (hitbox, position) {
                if (this.objectsToRaycastTouch === undefined) {
                    this.objectsToRaycastTouch = [hitbox];
                }
                else {
                    this.objectsToRaycastTouch.push(hitbox);
                }
                hitbox.attachTo(this, position);
                return this;
            };
        }
    }
}
export class RaycasterHandler {
    enabled;
    dragAndDropEnabled;
    dragAndDropViewportCheck = true; //used to avoid problems with viewport switch when drag
    intersectionSortComparer = (a, b) => { return a.distance - b.distance; };
    objectWithMouseHover;
    objectsIntercepted = [];
    mainObjIntercepted;
    objectDragging;
    firstTouchId;
    _cursorHandler;
    _dragUtility = new DragUtility();
    _raycaster = new Raycaster();
    _triggerButton;
    _triggerEventWhileDragging;
    _objectClickedArgs = [];
    _objectStartClickedArgs = [];
    _objLastClickedArgs = [];
    _mouseDown = [];
    _mouseUp = [];
    _mouseMove = [];
    _typeClickToRemove = [];
    get isTriggerClickClicked() { return this._mouseDown.indexOf(this._triggerButton) !== -1 || this._mouseDown.indexOf(ClickType.touch) !== -1; }
    get isTriggerClickReleased() { return this._mouseUp.indexOf(this._triggerButton) !== -1 || this._mouseUp.indexOf(ClickType.touch) !== -1; }
    constructor(enabled = true, enableDragAndDrop = true, triggerButton = MouseClickType.left, triggerEventWhileDragging = false, cursorEnabled = true) {
        RaycasterHandlerInitializer.edit3DObjectPrototype();
        this._cursorHandler = new CursorHandler(this, cursorEnabled);
        this._triggerButton = triggerButton;
        this._triggerEventWhileDragging = triggerEventWhileDragging;
        this.enabled = enabled;
        this.dragAndDropEnabled = enableDragAndDrop;
        Globals.canvas.addEventListener("mousedown", (event) => { this._mouseDown.push(event.which); });
        Globals.canvas.addEventListener("mousemove", (event) => { this._mouseMove.push(event.which); });
        document.addEventListener("mouseup", (event) => { this._mouseUp.push(event.which); });
        Globals.canvas.addEventListener("touchstart", (event) => {
            if (this.firstTouchId === undefined) {
                this.firstTouchId = event.touches[0].identifier;
                this._mouseDown.push(ClickType.touch);
            }
            event.preventDefault();
        });
        Globals.canvas.addEventListener("touchmove", (event) => {
            this.getTouch(event.changedTouches) && this._mouseMove.push(ClickType.touch);
        });
        document.addEventListener("touchend", (event) => {
            !this.getTouch(event.touches) && this._mouseUp.push(ClickType.touch);
            if (event.touches.length === 0) {
                this.firstTouchId = undefined;
            }
        });
        document.addEventListener("touchcancel", (event) => {
            !this.getTouch(event.touches) && this._mouseUp.push(ClickType.touch);
            if (event.touches.length === 0) {
                this.firstTouchId = undefined;
            }
        });
    }
    isMouseDown(typeclick) {
        return this._objectStartClickedArgs[typeclick] !== undefined;
    }
    deleteMouseUp() {
        this._mouseUp = [];
        this._mouseMove = [];
    }
    triggerEvents(meshesParent, camera, coords) {
        if (this.enabled) {
            this._raycaster.setFromCamera(coords, camera);
            this.trigger_BeforeDrag_DragMove_AfterDragMove(camera);
            this.objectsIntercepted = this.raycastObjects(meshesParent, this._raycaster, coords.isTouch);
            this.objectsIntercepted.sort(this.intersectionSortComparer);
            this.mainObjIntercepted = this.objectsIntercepted[0];
            let triggerPressEvents = !this.objectDragging || this._triggerEventWhileDragging;
            if (triggerPressEvents) {
                this.triggerMouseLeave(false);
                this.triggerMouseHover();
            }
            if (coords.lastTouch) { //when release touch, trigger leave event
                this.triggerMouseLeave(true);
            }
            this.triggerMouseMove();
            this.triggerMouseUp();
            triggerPressEvents && this.triggerMouseDown();
            this.triggerDoubleClick();
            this.triggerClick();
            triggerPressEvents && this.triggerMouseInterception();
            this.triggerDragEnd();
            this.triggerDragStart(camera);
            this._mouseDown = [];
            this._mouseUp = [];
            this._mouseMove = [];
            this.clearTypeClickBuffer();
            this._cursorHandler.update(this.objectDragging, this.mainObjIntercepted);
        }
    }
    triggerEndEvents() {
        if (this.objectDragging) {
            this._dragUtility.stopDragging();
            this.objectDragging.triggerEvent("onDragEnd", this.objectDragging);
            this.objectDragging.isDragging = false;
            this.objectDragging = undefined;
        }
        if (this.objectWithMouseHover) {
            this.objectWithMouseHover.triggerEvent("onLeave", this.objectWithMouseHover);
            this.objectWithMouseHover.isHovered = false;
            this.objectWithMouseHover = undefined;
        }
        for (const typeClick in this._objectClickedArgs) {
            const objClickedArgs = this._objectClickedArgs[typeClick];
            if (objClickedArgs?.object.areAllEnabled) {
                objClickedArgs.object.triggerEvent("onMouseUp", objClickedArgs);
                this._objectStartClickedArgs[typeClick].object.isPressed[typeClick] = false;
                this._objectClickedArgs[typeClick] = undefined;
                this._objectStartClickedArgs[typeClick] = undefined;
            }
        }
    }
    trigger_BeforeDrag_DragMove_AfterDragMove(camera) {
        if (this.dragAndDropEnabled && this.dragAndDropViewportCheck && this.isDragMoved()) {
            let position = this._dragUtility.doDragging(this._raycaster, this.objectDragging, camera);
            if (position && !position.equals(this.objectDragging.position)) {
                let argsBefore = { position: position, preventDefault: false, mesh: this.objectDragging };
                this.objectDragging.triggerEvent("onBeforeDragMove", argsBefore);
                let args = { position: position, vectorMouse: position.clone(), preventDefault: argsBefore.preventDefault, mesh: this.objectDragging };
                this.objectDragging.triggerEvent("onDragMove", args);
                if (!args.preventDefault) {
                    this.objectDragging.position.copy(position);
                }
                this.objectDragging.updateMatrixWorld(true);
                this.objectDragging.triggerEvent("onAfterDragMove", { position: args.position, vectorMouse: args.vectorMouse });
            }
        }
    }
    triggerMouseLeave(forceEventOnTouch) {
        if ((forceEventOnTouch && this.objectWithMouseHover) || (this.objectWithMouseHover && ((this.mainObjIntercepted && this.mainObjIntercepted.object !== this.objectWithMouseHover) || !this.mainObjIntercepted))) {
            this.objectWithMouseHover.triggerEvent("onLeave", this.objectWithMouseHover);
            this.objectWithMouseHover.isHovered = false;
            this.objectWithMouseHover = undefined;
        }
    }
    triggerMouseHover() {
        if (this.mainObjIntercepted && this.mainObjIntercepted.object !== this.objectWithMouseHover && this.mainObjIntercepted.object.areAllEnabled) {
            this.objectWithMouseHover = this.mainObjIntercepted.object;
            this.objectWithMouseHover.isHovered = true;
            this.objectWithMouseHover.triggerEvent("onHover", this.mainObjIntercepted);
        }
    }
    triggerMouseDown() {
        if (this.mainObjIntercepted?.object.areAllEnabled) {
            for (const typeClick of this._mouseDown) {
                this._objectStartClickedArgs[typeClick] = this.mainObjIntercepted;
                this._objectStartClickedArgs[typeClick].typeClick = typeClick;
                this._objectClickedArgs[typeClick] = this.mainObjIntercepted;
                this.mainObjIntercepted.object.isPressed[typeClick] = true;
                this.mainObjIntercepted.object.triggerEvent("onMouseDown", this._objectStartClickedArgs[typeClick]);
            }
        }
    }
    triggerMouseMove() {
        for (const typeClick of this._mouseMove) {
            const objStartClickedArgs = this._objectStartClickedArgs[typeClick];
            if (objStartClickedArgs && this.mainObjIntercepted) {
                this._objectClickedArgs[typeClick] = this.mainObjIntercepted;
                this._objectClickedArgs[typeClick].typeClick = typeClick;
                if (objStartClickedArgs.object === this.mainObjIntercepted.object) {
                    objStartClickedArgs.object.triggerEvent("onClickMove", this._objectClickedArgs[typeClick]);
                }
            }
        }
    }
    triggerMouseUp() {
        for (const typeClick of this._mouseUp) {
            const objStartClickedArgs = this._objectStartClickedArgs[typeClick];
            if (objStartClickedArgs) {
                objStartClickedArgs.object.triggerEvent("onMouseUp", this._objectClickedArgs[typeClick]);
                objStartClickedArgs.object.isPressed[typeClick] = false;
                this._typeClickToRemove.push(typeClick); //todo check se rimuovere il typeclicktoremove
            }
        }
    }
    triggerDragEnd() {
        if (this.objectDragging && this.isTriggerClickReleased) {
            this._dragUtility.stopDragging();
            this.objectDragging.triggerEvent("onDragEnd", this.objectDragging);
            this.objectDragging.isDragging = false;
            this.objectDragging = undefined;
        }
    }
    triggerDragStart(camera) {
        if (this.mainObjIntercepted && this.isTriggerClickClicked && this.mainObjIntercepted.object.areAllEnabled && this.mainObjIntercepted.object.isDraggable && this.dragAndDropEnabled && this.dragAndDropViewportCheck) { //todo create getter for both drag enabled state
            this.objectDragging = this.mainObjIntercepted.object;
            this._dragUtility.startDragging(this._raycaster, this.objectDragging, camera);
            this.objectDragging.isDragging = true;
            this.objectDragging.triggerEvent("onDragStart", this.objectDragging);
        }
    }
    triggerClick() {
        for (const typeClick of this._mouseUp) {
            const objStartClickedArgs = this._objectStartClickedArgs[typeClick]; //todo clone it ?
            if (objStartClickedArgs && this.mainObjIntercepted && objStartClickedArgs.object === this.mainObjIntercepted.object && objStartClickedArgs.object.areAllEnabled) {
                objStartClickedArgs.object.triggerEvent("onClick", this._objectClickedArgs[typeClick]);
            }
            this._objLastClickedArgs[typeClick] = objStartClickedArgs;
            objStartClickedArgs && (this._objLastClickedArgs[typeClick].dateClick = new Date());
        }
    }
    triggerDoubleClick() {
        for (const typeClick of this._mouseUp) {
            const objStartClickedArgs = this._objectStartClickedArgs[typeClick]; //todo clone it ?
            const objLastClickedArgs = this._objLastClickedArgs[typeClick];
            if (objStartClickedArgs && this.mainObjIntercepted && objStartClickedArgs.object === this.mainObjIntercepted.object && objStartClickedArgs.object.areAllEnabled &&
                objLastClickedArgs?.object == objStartClickedArgs.object && (new Date().getTime() - objLastClickedArgs.dateClick.getTime()) <= 300) {
                objStartClickedArgs.object.triggerEvent("onDoubleClick", this._objectClickedArgs[typeClick]);
            }
        }
    }
    triggerMouseInterception() {
        if (this.mainObjIntercepted?.object.areAllEnabled) {
            this.mainObjIntercepted.object.triggerEvent("onMouseInterception", this.mainObjIntercepted);
        }
    }
    clearTypeClickBuffer() {
        for (const typeClick of this._typeClickToRemove) {
            this._objectClickedArgs[typeClick] = undefined;
            this._objectStartClickedArgs[typeClick] = undefined;
        }
        this._typeClickToRemove = [];
    }
    getTouch(touches) {
        for (const touch of touches) {
            if (touch.identifier === this.firstTouchId) {
                return touch;
            }
        }
        return undefined;
    }
    isDragMoved() {
        for (const mouseMove of this._mouseMove) {
            if (mouseMove === this._triggerButton || mouseMove === ClickType.touch) {
                return true;
            }
        }
        return false;
    }
    raycastObjects(object, raycaster, isTouch) {
        let intersections = [];
        if (!object)
            return intersections;
        if (object.interceptedByRaycaster && object.visible) {
            for (const child of object.children) {
                intersections.push(...this.raycastObjects(child, raycaster, isTouch));
            }
            let tempIntersection = raycaster.intersectObjects((isTouch ? object.objectsToRaycastTouch : undefined) || object.objectsToRaycast || [object], false);
            for (const temp of tempIntersection) {
                temp.meshToRaycast = temp.object;
                temp.object = object;
                intersections.push(temp);
            }
        }
        return intersections;
    }
}
//# sourceMappingURL=RaycasterHandler.js.map